import { FC, useState, useEffect, useRef } from 'react'

import { TextInput, TextInputProps } from '@mantine/core'
import styled from 'styled-components'

import { colors } from 'src/Helpers/constants'

const StyledInput = styled(TextInput)`
  position: relative;
  width: 100%;

  input {
    height: 41px;
    border: 1px solid ${colors.secondGray};
    color: ${colors.thirdBlue};
    --input-placeholder-color: ${colors.thirdBlue};

    &::placeholder {
      opacity: 0.5;
      font-size: 12px;
    }
  }

  label {
    position: absolute;
    top: -7px;
    left: 10px;
    z-index: 1;
    width: fit-content;
    background-color: white;
    padding: 0 5px;
    font-size: 10px;
    color: ${colors.thirdGray};
    white-space: nowrap;
  }

  &[data-error] input {
    border-color: ${colors.red};
  }

  &[data-error] {
    .mantine-Select-wrapper {
      margin-bottom: 0px !important;
    }
  }

  .mantine-TextInput-error {
    position: absolute;
    bottom: -15px;
    font-size: 11px;
    text-align: left;
  }
`

export type InputProps = TextInputProps & {
  error?: string
}

// Helper function to format number with thousand separators
const formatNumberWithSeparators = (value: string): string => {
  // Remove all non-digit characters except minus sign at the beginning
  const cleanValue = value.replace(/[^\d-]/g, '').replace(/(?!^)-/g, '')

  if (!cleanValue || cleanValue === '-') return cleanValue

  // Handle negative numbers
  const isNegative = cleanValue.startsWith('-')
  const numberPart = cleanValue.replace('-', '')

  // Add thousand separators (space)
  const formatted = numberPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')

  return isNegative ? `-${formatted}` : formatted
}

// Helper function to get clean numeric value
const getCleanValue = (value: string): string => {
  return value.replace(/[^\d-]/g, '').replace(/(?!^)-/g, '')
}

export const Input: FC<InputProps> = ({ onChange, value, type, ...props }) => {
  const [displayValue, setDisplayValue] = useState<string>('')
  const inputRef = useRef<HTMLInputElement>(null)
  const [cursorPosition, setCursorPosition] = useState<number>(0)

  // Initialize display value
  useEffect(() => {
    if (type === 'money' && value !== undefined) {
      const stringValue = String(value)
      // Hide "0" for money fields and show placeholder instead
      if (value === 0 || value === '0') {
        setDisplayValue('')
      } else {
        setDisplayValue(formatNumberWithSeparators(stringValue))
      }
    } else if (type === 'number' && (value === 0 || value === '0')) {
      // Hide "0" for number fields and show placeholder instead
      setDisplayValue('')
    } else {
      setDisplayValue(String(value || ''))
    }
  }, [value, type])

  // Restore cursor position after formatting
  useEffect(() => {
    if (type === 'money' && inputRef.current) {
      inputRef.current.setSelectionRange(cursorPosition, cursorPosition)
    }
  }, [displayValue, cursorPosition, type])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const inputValue = event.target.value
    const currentCursorPos = event.target.selectionStart || 0

    if (type === 'money') {
      // Get clean value for the actual onChange callback
      const cleanValue = getCleanValue(inputValue)

      // Format for display
      const formattedValue = formatNumberWithSeparators(inputValue)
      setDisplayValue(formattedValue)

      // Calculate new cursor position accounting for added/removed spaces
      const spacesBeforeCursor = (
        inputValue.slice(0, currentCursorPos).match(/ /g) || []
      ).length
      const spacesInFormatted = (
        formattedValue.slice(0, currentCursorPos).match(/ /g) || []
      ).length
      const newCursorPos =
        currentCursorPos + (spacesInFormatted - spacesBeforeCursor)
      setCursorPosition(Math.max(0, newCursorPos))

      // Call original onChange with clean value, use 0 if empty
      if (onChange) {
        const valueToSend = cleanValue === '' ? '0' : cleanValue
        const syntheticEvent = {
          ...event,
          target: {
            ...event.target,
            value: valueToSend,
          },
        }
        onChange(syntheticEvent as React.ChangeEvent<HTMLInputElement>)
      }
    } else if (type === 'number') {
      setDisplayValue(inputValue)
      if (onChange) {
        // For number fields, send 0 if empty to maintain proper data structure
        const valueToSend = inputValue === '' ? '0' : inputValue
        const syntheticEvent = {
          ...event,
          target: {
            ...event.target,
            value: valueToSend,
          },
        }
        onChange(syntheticEvent as React.ChangeEvent<HTMLInputElement>)
      }
    } else {
      setDisplayValue(inputValue)
      if (onChange) {
        onChange(event)
      }
    }
  }

  if (type === 'money') {
    return (
      <StyledInput
        {...props}
        type='text'
        ref={inputRef}
        value={displayValue}
        onChange={handleChange}
      />
    )
  }

  if (type === 'number') {
    return (
      <StyledInput
        {...props}
        type='number'
        value={displayValue}
        onChange={handleChange}
      />
    )
  }

  return (
    <StyledInput {...props} value={value} onChange={onChange} type={type} />
  )
}
